<template>
  <div class="organisation-user-list">
    <div class="container col-lg-8 col-sm-12">
      <BaseListToolbar title="Users" @search="updateSearchValue" />
      <div v-if="isFetchingUsers">
        <v-skeleton-loader v-for="i in 10" :key="i" type="list-item-avatar" />
      </div>
      <div v-else>
        <v-list-item
          v-for="user in organisationUsers"
          :key="user.id"
          @click="() => handleUserSelect(user)"
        >
          <UserListItem :user="user" />
        </v-list-item>
        <v-pagination
          :total-visible="page.pageSize"
          v-if="showPagination"
          v-model="page.currentPage"
          @input="loadUsers"
          :length="page.lastPage"
        />
      </div>
    </div>
    <Permissioned section="settings">
      <EditUserAction
        v-if="isEditUserDialogOpen"
        :user="selectedUser"
        @close="isEditUserDialogOpen = false"
        :oneOfPermissions="editUserPermissions"
      />
    </Permissioned>
    <BaseFooter>
      <Permissioned section="settings" class="d-flex actions-bar">
        <NewUserAction
          :oneOfPermissions="addUserPermissions"
          class="full-width"
        />
      </Permissioned>
    </BaseFooter>
  </div>
</template>

<script>
import { SETTINGS_NAMESPACE } from "@/store/modules/settings";
import { createNamespacedHelpers } from "vuex";
import { FETCH_ORGANISATION_USERS } from "@/store/modules/settings/actions";
import { copyState } from "@/utils/helpers";
import NewUserAction from "@/views/settings/Users/actions/NewUser/NewUserAction";
import { AUTH_NAMESPACE } from "@/store/modules/auth";
import EditUserAction from "@/views/settings/Users/actions/EditUser/EditUserAction";
import UserListItem from "@/views/settings/Users/components/UserListItem";
import { get, size, omitBy, isEmpty } from "lodash";
import BaseListToolbar from "@/components/common/Toolbar/BaseListToolbar";
import BaseFooter from "@/components/common/BaseFooter";
import Permissioned from "@/components/common/Permissioned";
import { paginationMixin } from "@/mixins/pagination.mixin";

const { mapActions, mapState } = createNamespacedHelpers(SETTINGS_NAMESPACE);
const { mapState: mapAuthState } = createNamespacedHelpers(AUTH_NAMESPACE);

const NEW_USER_PERMISSIONS = [
  "new-user",
  "new-user.client",
  "new-user.msp",
  "new-user.agency"
];
const EDIT_USER_PERMISSIONS = [
  "edit-user",
  "edit-user.client",
  "edit-user.msp",
  "edit-user.agency"
];

export default {
  name: "OrganisationUsers",
  mixins: [paginationMixin],
  components: {
    EditUserAction,
    NewUserAction,
    BaseListToolbar,
    BaseFooter,
    UserListItem,
    Permissioned
  },
  data() {
    return {
      addUserPermissions: NEW_USER_PERMISSIONS,
      editUserPermissions: EDIT_USER_PERMISSIONS,
      isEditUserDialogOpen: false,
      selectedUser: null,
      search: "",
      attributeFilters: {},
      availableFilters: {}
    };
  },
  computed: {
    ...mapState(copyState(["organisationUsers", "isFetchingUsers"])),
    ...mapAuthState(copyState(["userData"])),
    selfOrganisationId() {
      return get(this.userData, "organisation.id");
    },
    requestParams() {
      return {
        filter: {
          organisation_id: this.selfOrganisationId,
          ...(size(this.search) && { search: this.search }),
          ...this.attributeFilters
        },
        ...this.requestPageParams
      };
    }
  },
  created() {
    if (this.selfOrganisationId) {
      this.loadUsers();
    }
  },
  methods: {
    ...mapActions({
      fetchOrganisationUsers: FETCH_ORGANISATION_USERS
    }),
    async loadUsers() {
      const data = await this.fetchOrganisationUsers(this.requestParams);
      if (data) {
        this.adjustPageDetails(data.meta);
      }
    },
    adjustPageDetails({ page, filters }) {
      this.updatePageDetails({ page });
      this.availableFilters = filters;
    },
    updateSearchValue(text) {
      this.search = text;
      this.loadUsers();
    },
    handleUserSelect(user) {
      this.selectedUser = user;
      this.isEditUserDialogOpen = true;
    },
    updateFilters(filters) {
      const clearedFilters = this.cleanEmptyFilters(filters);
      this.attributeFilters = { ...clearedFilters };
      this.resetPagination();
      this.loadUsers();
    },
    cleanEmptyFilters(filters) {
      return omitBy(filters, isEmpty);
    },
    clearFilters() {
      this.attributeFilters = {};
      this.resetPagination();
      this.loadUsers();
    }
  }
};
</script>

<style lang="scss"></style>
